import React, {
    useMemo,
    useRef,
    useState,
    useCallback,
    useEffect,
} from "react";
import {
    FileText,
    X,
    GripHorizontal,
    ExternalLink,
    Loader,
} from "lucide-react";
import { renderAsync } from "docx-preview";
import "./PreviewPanel.css";

interface Segment {
    id: string;
    segment_order: number;
    source_text: string;
    target_text: string;
    status: string;
    page_number?: number | null;
}

interface PreviewPanelProps {
    segments: Segment[];
    onSegmentClick: (segmentId: string) => void;
    onClose: () => void;
    targetLanguage?: string;
    fileUrl?: string | null;
}

const MIN_W = 500;
const MIN_H = 400;
const DEFAULT_W = 780;
const DEFAULT_H = 880;

const PreviewPanel: React.FC<PreviewPanelProps> = ({
    segments,
    onSegmentClick,
    onClose,
    targetLanguage,
    fileUrl,
}) => {
    const [pos, setPos] = useState({
        x: Math.max(20, window.innerWidth / 2 - DEFAULT_W / 2),
        y: 60,
    });
    const [size, setSize] = useState({ w: DEFAULT_W, h: DEFAULT_H });
    const [loading, setLoading] = useState(false);
    const [docxRendered, setDocxRendered] = useState(false);
    const [renderError, setRenderError] = useState<string | null>(null);
    const dragging = useRef(false);
    const resizing = useRef(false);
    const dragOffset = useRef({ x: 0, y: 0 });
    const resizeStart = useRef({ x: 0, y: 0, w: DEFAULT_W, h: DEFAULT_H });
    const docxContainerRef = useRef<HTMLDivElement>(null);
    const segmentsRef = useRef(segments);
    const onSegmentClickRef = useRef(onSegmentClick);
    useEffect(() => {
        segmentsRef.current = segments;
    }, [segments]);
    useEffect(() => {
        onSegmentClickRef.current = onSegmentClick;
    }, [onSegmentClick]);

    // Render DOCX into container on mount if fileUrl is a DOCX
    useEffect(() => {
        if (!fileUrl || !fileUrl.toLowerCase().includes(".docx")) return;
        if (!docxContainerRef.current) return;

        const render = async () => {
            setLoading(true);
            setRenderError(null);
            try {
                const { exportDocxXml } =
                    await import("@/lib/cat/docxXmlParser");
                const translations: Record<number, string> = {};
                segments.forEach((seg) => {
                    if (seg.segment_order != null && seg.target_text?.trim()) {
                        translations[seg.segment_order] = seg.target_text;
                    }
                });
                const blob = await exportDocxXml(fileUrl, translations);
                if (docxContainerRef.current) {
                    await renderAsync(
                        blob,
                        docxContainerRef.current,
                        undefined,
                        {
                            className: "docx",
                            inWrapper: true,
                            ignoreWidth: false,
                            ignoreHeight: false,
                            ignoreFonts: false,
                            breakPages: true,
                            useBase64URL: true,
                        },
                    );
                    // Override docx-preview inline styles via injected stylesheet

                    if (docxContainerRef.current) {
                        const styleTag = document.createElement("style");

                        styleTag.textContent = [
                            ".docx-preview-container section.docx { background: #fff !important; }",

                            ".docx-preview-container .docx-wrapper { background: #e2e8f0 !important; padding: 24px !important; }",

                            ".docx-preview-container td { background: #fff !important; }",

                            ".docx-preview-container th { background: #fff !important; }",

                            ".docx-preview-container img { max-width: none !important; width: auto !important; }",
                        ].join(" ");

                        docxContainerRef.current.appendChild(styleTag);
                    }

                    // Strip only background from table elements — preserve width/padding
                    if (docxContainerRef.current) {
                        const els =
                            docxContainerRef.current.querySelectorAll(
                                "td, th, tr, table",
                            );
                        els.forEach((el) => {
                            const h = el as HTMLElement;
                            h.style.removeProperty("background-color");
                            h.style.removeProperty("background");
                        });
                    }
                    // Wire click-to-segment on the rendered docx DOM
                    if (docxContainerRef.current) {
                        docxContainerRef.current.addEventListener(
                            "click",
                            (e) => {
                                // Walk up DOM to find paragraph-level element
                                let el = e.target as HTMLElement | null;
                                while (
                                    el &&
                                    ![
                                        "P",
                                        "LI",
                                        "TD",
                                        "H1",
                                        "H2",
                                        "H3",
                                        "H4",
                                    ].includes(el.tagName)
                                ) {
                                    el = el.parentElement;
                                }
                                if (!el) return;
                                const clickedText = el.innerText?.trim();
                                if (!clickedText || clickedText.length < 5)
                                    return;
                                // Use refs to get latest segments and callback — avoids stale closure
                                const currentSegments = segmentsRef.current;
                                const match = currentSegments.find(
                                    (seg) =>
                                        (seg.target_text?.trim() &&
                                            clickedText.includes(
                                                seg.target_text
                                                    .trim()
                                                    .slice(0, 40),
                                            )) ||
                                        (seg.source_text?.trim() &&
                                            clickedText.includes(
                                                seg.source_text
                                                    .trim()
                                                    .slice(0, 40),
                                            )),
                                );
                                if (match) onSegmentClickRef.current(match.id);
                            },
                        );
                    }
                    setDocxRendered(true);
                }
            } catch (err: any) {
                console.error("docx-preview error:", err);
                setRenderError(
                    err?.message || "Failed to render document preview",
                );
            } finally {
                setLoading(false);
            }
        };

        render();
    }, [fileUrl]);

    // Listen for postMessage from pop-out window
    useEffect(() => {
        const handler = (e: MessageEvent) => {
            if (e.data?.type !== "PREVIEW_SEGMENT_CLICK") return;
            const text = e.data.text as string;
            if (!text || !segmentsRef.current) return;
            const idx = segmentsRef.current.findIndex(
                (s) =>
                    s.source_text?.slice(0, 80).includes(text.slice(0, 40)) ||
                    s.target_text?.slice(0, 80).includes(text.slice(0, 40)),
            );
            if (idx !== -1 && onSegmentClickRef.current) {
                onSegmentClickRef.current(segmentsRef.current[idx].id);
            }
        };
        window.addEventListener("message", handler);
        return () => window.removeEventListener("message", handler);
    }, []);

    // Drag
    const onDragMouseDown = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            dragging.current = true;
            dragOffset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
        },
        [pos],
    );

    // Resize
    const onResizeMouseDown = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            resizing.current = true;
            resizeStart.current = {
                x: e.clientX,
                y: e.clientY,
                w: size.w,
                h: size.h,
            };
        },
        [size],
    );

    useEffect(() => {
        const onMove = (e: MouseEvent) => {
            if (dragging.current) {
                setPos({
                    x: Math.max(
                        0,
                        Math.min(
                            window.innerWidth - size.w,
                            e.clientX - dragOffset.current.x,
                        ),
                    ),
                    y: Math.max(0, e.clientY - dragOffset.current.y),
                });
            }
            if (resizing.current) {
                setSize({
                    w: Math.max(
                        MIN_W,
                        resizeStart.current.w +
                            (e.clientX - resizeStart.current.x),
                    ),
                    h: Math.max(
                        MIN_H,
                        resizeStart.current.h +
                            (e.clientY - resizeStart.current.y),
                    ),
                });
            }
        };
        const onUp = () => {
            dragging.current = false;
            resizing.current = false;
        };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
        };
    }, [size.w]);

    const grouped = useMemo(() => {
        const hasPaging = segments.some((s) => s.page_number != null);
        if (!hasPaging) return [{ page: null, segments }];
        const map = new Map<number, Segment[]>();
        for (const seg of segments) {
            const page = seg.page_number ?? 1;
            if (!map.has(page)) map.set(page, []);
            map.get(page)!.push(seg);
        }
        return Array.from(map.entries())
            .sort((a, b) => a[0] - b[0])
            .map(([page, segs]) => ({ page, segments: segs }));
    }, [segments]);

    const translated = segments.filter((s) => s.target_text?.trim()).length;
    const pct =
        segments.length > 0
            ? Math.round((translated / segments.length) * 100)
            : 0;
    const isDocx = fileUrl && fileUrl.toLowerCase().includes(".docx");

    const handlePopOut = () => {
        if (!docxContainerRef.current || !isDocx) return;
        const html = docxContainerRef.current.innerHTML;
        const win = window.open(
            "",
            "_blank",
            "width=900,height=1000,menubar=no,toolbar=no,location=no",
        );
        if (win) {
            // Copy all docx-preview stylesheets into the new window
            const styles = Array.from(document.styleSheets)
                .map((ss) => {
                    try {
                        return Array.from(ss.cssRules)
                            .map((r) => r.cssText)
                            .join("\n");
                    } catch {
                        return "";
                    }
                })
                .join("\n");
            win.document
                .write(`<!DOCTYPE html><html><head><title>Document Preview</title>
        <style>
          body { background: #e2e8f0; margin: 0; padding: 24px; }
          ${styles}
        </style>
      </head><body>${html}</body></html>`);
            win.document.close();
            win.document.addEventListener("click", (e) => {
                const el = e.target as HTMLElement;
                const p = el.closest("p, li, td, h1, h2, h3, h4");
                if (!p) return;
                const text = p.textContent?.trim().slice(0, 80) ?? "";
                window.opener.postMessage(
                    { type: "PREVIEW_SEGMENT_CLICK", text },
                    "*",
                );
            });
            onClose();
        }
    };

    return (
        <div
            style={{
                position: "fixed",
                left: pos.x,
                top: pos.y,
                width: size.w,
                height: size.h,
                zIndex: 9999,
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 25px 60px rgba(0,0,0,0.5)",
                borderRadius: 10,
                overflow: "hidden",
                border: "1px solid #334155",
            }}
        >
            {/* Title bar */}
            <div
                onMouseDown={onDragMouseDown}
                style={{
                    background: "#1e293b",
                    borderBottom: "1px solid #334155",
                    padding: "8px 12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "grab",
                    userSelect: "none",
                    flexShrink: 0,
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <GripHorizontal size={14} color="#94a3b8" />
                    <FileText size={14} color="#eab308" />
                    <span
                        style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#e2e8f0",
                            letterSpacing: "0.05em",
                            textTransform: "uppercase",
                        }}
                    >
                        Document Preview
                    </span>
                    {targetLanguage && (
                        <span
                            style={{
                                fontSize: 10,
                                color: "#64748b",
                                marginLeft: 4,
                            }}
                        >
                            {targetLanguage.toUpperCase()}
                        </span>
                    )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 11, color: "#64748b" }}>
                        {translated}/{segments.length} · {pct}%
                    </span>
                    {isDocx && docxRendered && (
                        <button
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={handlePopOut}
                            title="Open in separate window"
                            style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                color: "#64748b",
                                padding: 2,
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            <ExternalLink size={14} />
                        </button>
                    )}
                    <button
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={onClose}
                        style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "#64748b",
                            padding: 2,
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <X size={16} />
                    </button>
                </div>
            </div>

            {/* Progress bar */}
            <div style={{ height: 2, background: "#0f172a", flexShrink: 0 }}>
                <div
                    style={{
                        height: 2,
                        background: "#eab308",
                        width: `${pct}%`,
                        transition: "width 0.3s",
                    }}
                />
            </div>

            {/* Content */}
            <div
                style={{
                    flex: 1,
                    overflowY: "auto",
                    background: "#e2e8f0",
                    padding: "24px 20px",
                    position: "relative",
                }}
            >
                {/* Loading spinner */}
                {loading && (
                    <div
                        style={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "rgba(226,232,240,0.9)",
                            zIndex: 10,
                        }}
                    >
                        <Loader
                            size={32}
                            color="#eab308"
                            className="animate-spin"
                        />
                        <span
                            style={{
                                marginTop: 12,
                                fontSize: 13,
                                color: "#475569",
                            }}
                        >
                            Rendering document...
                        </span>
                    </div>
                )}

                {/* Error state */}
                {renderError && (
                    <div
                        style={{
                            background: "#fee2e2",
                            border: "1px solid #fca5a5",
                            borderRadius: 8,
                            padding: "12px 16px",
                            marginBottom: 16,
                            fontSize: 13,
                            color: "#991b1b",
                        }}
                    >
                        {renderError}
                    </div>
                )}

                {/* DOCX rendered output */}
                {isDocx && (
                    <div
                        ref={docxContainerRef}
                        style={{ background: "transparent", cursor: "pointer" }}
                    />
                )}

                {/* Plain text fallback for PDF/TXT/XLIFF */}
                {!isDocx &&
                    grouped.map(({ page, segments: pageSegs }) => (
                        <div key={page ?? "all"}>
                            <div
                                style={{
                                    background: "#fff",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                    borderRadius: 2,
                                    padding: "48px 52px",
                                    marginBottom: 24,
                                    fontFamily: "Georgia, serif",
                                    fontSize: 13.5,
                                    lineHeight: 1.75,
                                    color: "#1a1a1a",
                                }}
                            >
                                {page != null && (
                                    <div
                                        style={{
                                            fontSize: 10,
                                            color: "#94a3b8",
                                            textTransform: "uppercase",
                                            letterSpacing: "0.1em",
                                            marginBottom: 24,
                                            paddingBottom: 8,
                                            borderBottom: "1px solid #e2e8f0",
                                        }}
                                    >
                                        Page {page}
                                    </div>
                                )}
                                {pageSegs.map((seg) => {
                                    const hasTranslation =
                                        seg.target_text?.trim().length > 0;
                                    const text = hasTranslation
                                        ? seg.target_text
                                        : seg.source_text;
                                    return (
                                        <p
                                            key={seg.id}
                                            onClick={() =>
                                                onSegmentClick(seg.id)
                                            }
                                            style={{
                                                margin: "0 0 12px 0",
                                                padding: "2px 4px",
                                                borderRadius: 2,
                                                cursor: "pointer",
                                                color: hasTranslation
                                                    ? "#1a1a1a"
                                                    : "#94a3b8",
                                                fontStyle: hasTranslation
                                                    ? "normal"
                                                    : "italic",
                                                transition: "background 0.15s",
                                            }}
                                            onMouseEnter={(e) =>
                                                (e.currentTarget.style.background =
                                                    "#fefce8")
                                            }
                                            onMouseLeave={(e) =>
                                                (e.currentTarget.style.background =
                                                    "transparent")
                                            }
                                            dangerouslySetInnerHTML={{
                                                __html: text,
                                            }}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    ))}

                <div style={{ height: 32 }} />
            </div>

            {/* Resize handle */}
            <div
                onMouseDown={onResizeMouseDown}
                style={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    width: 20,
                    height: 20,
                    cursor: "nwse-resize",
                }}
            >
                <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    style={{ position: "absolute", bottom: 2, right: 2 }}
                >
                    <path
                        d="M14 4 L4 14 M14 9 L9 14 M14 14 L14 14"
                        stroke="#475569"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                    />
                </svg>
            </div>
        </div>
    );
};

export default PreviewPanel;
